import { getDisplayToken } from '@/domain/hooks/useDisplayToken'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { SuccessViewCheckmark } from '@/features/dialogs/common/components/success-view/SuccessViewCheckmark'
import { SuccessViewContent } from '@/features/dialogs/common/components/success-view/SuccessViewContent'
import { SuccessViewProceedButton } from '@/features/dialogs/common/components/success-view/SuccessViewProceedButton'
import { SuccessViewSummaryPanel } from '@/features/dialogs/common/components/success-view/SuccessViewSummaryPanel'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { Typography } from '@/ui/atoms/typography/Typography'
import { useChainId } from 'wagmi'

export interface SwapDebtSuccessViewProps {
  fromToken: Token
  fromAmount: NormalizedUnitNumber
  toToken: Token
  toAmount: NormalizedUnitNumber
  onProceed: () => void
  proceedText?: string
}

export function SwapDebtSuccessView({
  fromToken,
  fromAmount,
  toToken,
  toAmount,
  onProceed,
  proceedText = 'Done',
}: SwapDebtSuccessViewProps) {
  const chainId = useChainId()
  const displayFromToken = getDisplayToken(chainId, fromToken)
  const displayToToken = getDisplayToken(chainId, toToken)

  return (
    <SuccessViewContent>
      <SuccessViewCheckmark isModal />

      <SuccessViewSummaryPanel title="Debt Swapped">
        <div className="flex items-center gap-2">
          <TokenIcon token={fromToken} className="h-6" />
          <span className="text-muted-foreground text-sm">{displayFromToken.symbol}</span>
        </div>
        <div className="flex grow flex-col">
          <Typography className="text-right font-primary">{fromToken.format(fromAmount, { style: 'auto' })}</Typography>
        </div>
      </SuccessViewSummaryPanel>

      <div className="flex justify-center py-1">
        <svg className="h-4 w-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
        </svg>
      </div>

      <SuccessViewSummaryPanel title="New Debt">
        <div className="flex items-center gap-2">
          <TokenIcon token={toToken} className="h-6" />
          <span className="font-semibold">{displayToToken.symbol}</span>
        </div>
        <div className="flex grow flex-col">
          <Typography className="text-right font-primary">{toToken.format(toAmount, { style: 'auto' })}</Typography>
          <Typography variant="prompt" className="text-right">
            {toToken.formatUSD(toAmount)}
          </Typography>
        </div>
      </SuccessViewSummaryPanel>

      <SuccessViewProceedButton onProceed={onProceed}>{proceedText}</SuccessViewProceedButton>
    </SuccessViewContent>
  )
}
